import React, { useEffect, useState } from 'react';
import { AlertCircleIcon, CheckIcon, GlobeIcon, Loader2Icon } from 'lucide-react';
import { Button } from '../../components/ui/Button';
import { TextField } from '../../components/ui/Field';
import { SecuritySection } from '../../components/admin/SecuritySection';
import { useAdminData } from '../../contexts/AdminDataContext';
import { CONTACT_EMAIL } from '../../data/site';
export function Settings() {
  const {
    settings,
    settingsLoading,
    saveSettings
  } = useAdminData();
  const [draft, setDraft] = useState(settings);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState('');
  useEffect(() => {
    setDraft(settings);
  }, [settings]);
  const dirty = JSON.stringify(draft) !== JSON.stringify(settings);
  function field(key: keyof typeof draft) {
    return {
      value: draft[key],
      onChange: (event: React.ChangeEvent<HTMLInputElement>) => {
        setDraft({
          ...draft,
          [key]: event.target.value
        });
        setSaved(false);
        setError('');
      }
    };
  }
  return <div className="px-5 py-7 md:px-8 md:py-10" data-id="element-1402" data-mp="2erz6e60g8uj3">
      <p className="text-sm text-ink-muted" data-id="element-1403" data-mp="2etaa65twa2a7">Firmaoplysninger, kontakt og standardpriser</p>
      <h1 className="mt-1 font-display text-[clamp(1.8rem,5vw,2.5rem)] font-bold leading-[1.05] tracking-tight text-ink" data-id="element-1404" data-mp="2ssa15k4vqt9j">
        Indstillinger
      </h1>
      <p className="mt-3 max-w-2xl text-[15px] text-ink-soft" data-id="element-1405" data-mp="31rx2k5xlzz2l">
        Oplysningerne herunder er hjemmesidens ene fælles kilde. De bruges automatisk i topbjælken,
        hero-sektionen, kontaktafsnittet og footeren — du retter dem kun ét sted.
      </p>

      <p className="mt-5 flex max-w-2xl items-start gap-2 rounded-xl border border-line bg-white px-4 py-3 text-sm text-ink-soft" data-id="element-1406" data-mp="3bh6rxe12j3n0">
        <GlobeIcon className="mt-0.5 h-4 w-4 shrink-0 text-accent" aria-hidden="true" data-id="element-1407" data-mp="3lg5ssaflduzg" />
        Oplysningerne gemmes globalt og vises for alle besøgende på alle enheder.
      </p>

      <form className="mt-7 max-w-2xl space-y-5" onSubmit={async event => {
      event.preventDefault();
      if (saving) return;
      setSaving(true);
      const result = await saveSettings(draft);
      setSaving(false);
      if (result.ok) {
        setSaved(true);
        setError('');
      } else {
        setSaved(false);
        setError(result.error ?? 'Ændringerne kunne ikke gemmes.');
      }
    }} data-id="element-1408" data-mp="3geylsdl9wztu">
        <section className="rounded-card border border-line bg-white p-6" data-id="element-1409" data-mp="b2l1yy7fmenh">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1410" data-mp="f34vlrssr04v">Virksomhed</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1411" data-mp="k4u9df7elrwn">
            <TextField id="in-navn" label="Firmanavn" {...field('companyName')} data-id="element-1412" data-mp="p2dmucwa20nj" />
            <TextField id="in-cvr" label="CVR-nummer" inputMode="numeric" {...field('cvr')} data-id="element-1413" data-mp="p09jbw2aj3ns" />
            <TextField id="in-tagline" label="Tagline" className="sm:col-span-2" {...field('tagline')} data-id="element-1414" data-mp="2lx0864n8eutf" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1415" data-mp="2jazi4fbm1677">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1416" data-mp="2ejrddwl8690d">Kontaktoplysninger</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1417" data-mp="29kepr9mj4xwq">
            <TextField id="in-tlf" label="Telefonnummer" type="tel" inputMode="tel" hint="Vises i topbjælken, hero, kontakt og footer." {...field('phone')} data-id="element-1418" data-mp="29kxwrvan61vz" />
            <TextField id="in-mail" label="E-mail" type="email" readOnly disabled value={CONTACT_EMAIL} onChange={() => undefined} hint="Fast adresse — kan ikke ændres." data-id="element-1419" data-mp="2x9pkoah77y2y" />
            <TextField id="in-adresse" label="Adresse" className="sm:col-span-2" {...field('address')} data-id="element-1420" data-mp="1gwijpeb2ltna" />
            <TextField id="in-web" label="Hjemmeside" className="sm:col-span-2" {...field('website')} data-id="element-1421" data-mp="1bweam9chs173" />
          </div>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1422" data-mp="30piqhfii7fzh">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1423" data-mp="35iva1b8bswld">Standardpriser</h2>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1424" data-mp="2qtysmnkdq783">
            <TextField id="in-timepris" label="Timepris (kr.)" inputMode="numeric" {...field('hourlyRate')} data-id="element-1425" data-mp="2vs0efg96wkn9" />
            <TextField id="in-udkald" label="Udkaldsgebyr (kr.)" inputMode="numeric" {...field('callOutFee')} data-id="element-1426" data-mp="3jhupptdv215e" />
          </div>
          <p className="mt-4 text-sm text-ink-muted" data-id="element-1427" data-mp="39jnkg7sxp7j8">
            Bruges som forslag, når du opretter en ny sag. Du kan altid ændre prisen på den enkelte sag.
          </p>
        </section>

        <section className="rounded-card border border-line bg-white p-6" data-id="element-1428" data-mp="1y48fagqkn9d1">
          <h2 className="font-display text-lg font-bold tracking-tight text-ink" data-id="element-1429" data-mp="1y6cqljqsvanc">Faktura</h2>
          <p className="mt-1.5 text-sm text-ink-muted" data-id="element-1430" data-mp="234mo40zzync4">
            Standardværdier der kopieres ind, når du opretter en ny faktura. De kan altid rettes på
            den enkelte faktura, og ændringer her påvirker ikke fakturaer, du allerede har oprettet.
          </p>
          <div className="mt-5 grid gap-5 sm:grid-cols-2" data-id="element-1431" data-mp="u5d140mqib3d">
            <TextField id="in-bank" label="Bank" {...field('invoiceBankName')} data-id="element-1432" data-mp="q4t550dwddit" />
            <TextField id="in-regnr" label="Reg.nr." inputMode="numeric" {...field('invoiceRegistrationNumber')} data-id="element-1433" data-mp="q2ykzoag9pfs" />
            <TextField id="in-kontonr" label="Kontonr." inputMode="numeric" {...field('invoiceAccountNumber')} data-id="element-1434" data-mp="3tncoteiifb20" />
            <TextField id="in-betalingsbetingelser" label="Betalingsbetingelser" {...field('invoicePaymentTerms')} data-id="element-1435" data-mp="6g0n8nggpeuq" />
            <TextField id="in-betalingstekst" label="Betalingstekst" className="sm:col-span-2" {...field('invoicePaymentText')} data-id="element-1436" data-mp="3avjungrpfjsp" />
            <TextField id="in-fodnote" label="Standard bemærkning" className="sm:col-span-2" hint="Foreslås som bemærkning til kunden på nye fakturaer." {...field('invoiceFooterNote')} data-id="element-1437" data-mp="1isz6lmfrh3d9" />
          </div>
        </section>

        <div className="flex flex-wrap items-center gap-4" data-id="element-1438" data-mp="uwbixcnm0ut4">
          <Button type="submit" size="lg" disabled={!dirty || saving || settingsLoading} data-id="element-1439" data-mp="uxwd3160ibqg">
            {saving ? <>
                <Loader2Icon className="h-5 w-5 animate-spin" aria-hidden="true" data-id="element-1440" data-mp="14tg87lcr56e5" />
                Gemmer …
              </> : 'Gem ændringer'}
          </Button>

          {settingsLoading && <p className="text-[15px] text-ink-muted" data-id="element-1441" data-mp="2g838fazmnuhe">Henter aktuelle oplysninger …</p>}

          {dirty && !saving && !saved && !error && <p className="text-[15px] text-ink-muted" data-id="element-1442" data-mp="2l9shil3ahy2u">Der er ændringer, der ikke er gemt.</p>}

          {saved && <p role="status" className="flex items-center gap-2 text-[15px] text-ink" data-id="element-1443" data-mp="4kzrq4qq79s8">
              <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-accent-soft text-accent-deep" data-id="element-1444" data-mp="3vzxdride3ww7">
                <CheckIcon className="h-4 w-4" aria-hidden="true" data-id="element-1445" data-mp="3r02nnh1fvbc7" />
              </span>
              Ændringerne er gemt og opdateret på hjemmesiden
            </p>}

          {error && <p role="alert" className="flex items-start gap-2 text-[15px] text-danger" data-id="element-1446" data-mp="3hzcr2b4ctgxf">
              <AlertCircleIcon className="mt-0.5 h-4 w-4 shrink-0" aria-hidden="true" data-id="element-1447" data-mp="3d0jgorkd661h" />
              {error}
            </p>}
        </div>
      </form>

      <SecuritySection data-id="element-1448" data-mp="1b9y7ddbztvvp" />
    </div>;
}